// Field + preset config for the Payments FilterBuilder.
//
// Only these columns are exposed. `id` is deliberately excluded: the server
// reserves it for the keyset cursor, so a client `id` condition would be
// silently overwritten. The serializer (serializeWhere.js) enforces the same
// allow-list defensively.
//
// The old `?q=` free-text search matched reference and payment_ref. That remains
// reachable here: build an $or over reference/payment_ref with $like, or filter
// either field individually.

export const PAYMENT_STATES = [
  'NEW',
  'OTP_REQUIRED',
  'DEBIT_REQUIRED',
  'PIN_REQUIRED',
  'PHONE_REQUIRED',
  'BIRTHDAY_REQUIRED',
  'ADDRESS_REQUIRED',
  'STATUS_REQUIRED',
  'BANK_AUTHENTICATION_REQUIRED',
  'FAILED',
  'SUCCESS',
];

export const PAYMENT_STATUSES = [
  'PENDING',
  'PROCESSING',
  'FAILED',
  'SUCCESSFUL',
  'CANCELLED',
  'DEBT_OWED',
  'REFUND_INITIATED',
  'REFUNDED',
];

// Relative-date literals the API substitutes server-side (sqlize-query). Offered
// as presets on datetime fields so users can filter e.g. "last 24 hours" without
// picking an absolute timestamp. Sent verbatim as the condition value.
export const DATE_PRESETS = [
  { value: '@NOW', label: 'Now' },
  { value: '@LAST_10_MINUTES', label: 'Last 10 minutes' },
  { value: '@LAST_24_HOURS', label: 'Last 24 hours' },
  { value: '@TODAY', label: 'Today' },
  { value: '@YESTERDAY', label: 'Yesterday' },
  { value: '@THIS_WEEK', label: 'This week' },
  { value: '@LAST_WEEK', label: 'Last week' },
  { value: '@THIS_MONTH', label: 'This month' },
  { value: '@LAST_MONTH', label: 'Last month' },
];

// Reuses the shared FilterBuilder component. Operators per field come from the
// shared type-keyed OPS catalog in FilterBuilder.js (string/number/date/enum).
// `presets` on a date field is an optional, backward-compatible extension read
// only by FilterBuilder's value input.
export const PAYMENT_FIELDS = [
  { key: 'reference', label: 'Reference', type: 'string' },
  { key: 'payment_ref', label: 'Payment Ref', type: 'string' },
  { key: 'amount_due', label: 'Amount Due', type: 'number' },
  { key: 'amount_paid', label: 'Amount Paid', type: 'number' },
  { key: 'method', label: 'Method', type: 'string' },
  { key: 'processor', label: 'Processor', type: 'string' },
  { key: 'state', label: 'State', type: 'enum', options: PAYMENT_STATES },
  { key: 'status', label: 'Status', type: 'enum', options: PAYMENT_STATUSES },
  { key: 'customer_id', label: 'Customer ID', type: 'number' },
  { key: 'order_id', label: 'Order ID', type: 'number' },
  { key: 'created_at', label: 'Created', type: 'date', presets: DATE_PRESETS },
  { key: 'updated_at', label: 'Updated', type: 'date', presets: DATE_PRESETS },
];

// Column allow-list for the serializer — never contains `id`.
export const ALLOWED_FIELDS = PAYMENT_FIELDS.map((f) => f.key);
